import { TimingBreakdownBar } from '@/app/pages/Diagnostics/TimingBreakdownBar'
import { CLIENT_LABELS } from '@/app/pages/Diagnostics/diagnosticsUtils'
import { TruncatedCell } from '@/components/common-table/TruncatedCell'
import { Badge } from '@/components/ui/badge'
import type { ApiMetric } from '@/stores/diagnosticsStore'
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { format } from 'date-fns'

const columnHelper = createColumnHelper<ApiMetric>()

const formatMs = (value: number | undefined): string => {
	if (value === undefined || Number.isNaN(value)) {
		return '-'
	}
	if (value === 0) {
		return '0 ms'
	}
	if (value < 1) {
		return '<1 ms'
	}
	return `${Math.round(value)} ms`
}

const isSlow = (duration: number): boolean => duration > 500

export const getDiagnosticsColumns = (): ColumnDef<ApiMetric>[] => {
	const columns: ColumnDef<ApiMetric>[] = []

	columns.push(
		columnHelper.accessor('method', {
			header: 'Method',
			size: 110,
			meta: { fixedWidth: true },
			cell: info => (
				<Badge variant='outline' className='font-mono'>
					{info.getValue()}
				</Badge>
			),
		}) as ColumnDef<ApiMetric>,
	)

	columns.push(
		columnHelper.accessor('url', {
			header: 'URL',
			size: 360,
			meta: { fixedWidth: true },
			cell: info => <TruncatedCell maxWidth={360}>{info.getValue()}</TruncatedCell>,
		}) as ColumnDef<ApiMetric>,
	)

	columns.push(
		columnHelper.accessor('status', {
			header: 'Status',
			size: 90,
			meta: { fixedWidth: true },
			cell: info => {
				const status = info.getValue()
				if (status === undefined) {
					return <Badge variant='destructive'>ERR</Badge>
				}
				const variant: 'default' | 'destructive' | 'warning' =
					status >= 500 ? 'destructive' : status >= 400 ? 'warning' : 'default'
				return <Badge variant={variant}>{status}</Badge>
			},
		}) as ColumnDef<ApiMetric>,
	)

	columns.push(
		columnHelper.accessor('duration', {
			header: 'Duration',
			size: 110,
			sortDescFirst: true,
			meta: { fixedWidth: true },
			cell: info => {
				const value = info.getValue()
				return (
					<span className={isSlow(value) ? 'text-(--text-warning) font-medium' : undefined}>{formatMs(value)}</span>
				)
			},
		}) as ColumnDef<ApiMetric>,
	)

	columns.push(
		columnHelper.display({
			id: 'breakdown',
			header: 'Breakdown',
			size: 160,
			meta: { fixedWidth: true },
			cell: info => <TimingBreakdownBar metric={info.row.original} />,
		}) as ColumnDef<ApiMetric>,
	)

	columns.push(
		columnHelper.accessor('ttfb', {
			header: 'Server',
			size: 90,
			sortDescFirst: true,
			meta: { fixedWidth: true },
			cell: info => <span>{formatMs(info.getValue())}</span>,
		}) as ColumnDef<ApiMetric>,
	)

	columns.push(
		columnHelper.accessor('download', {
			header: 'Transfer',
			size: 90,
			sortDescFirst: true,
			meta: { fixedWidth: true },
			cell: info => <span>{formatMs(info.getValue())}</span>,
		}) as ColumnDef<ApiMetric>,
	)

	columns.push(
		columnHelper.display({
			id: 'overhead',
			header: 'Overhead',
			size: 90,
			meta: { fixedWidth: true },
			cell: info => {
				const m = info.row.original
				const overhead = Math.max(0, m.duration - (m.ttfb ?? 0) - (m.download ?? 0))
				return <span>{formatMs(overhead)}</span>
			},
		}) as ColumnDef<ApiMetric>,
	)

	columns.push(
		columnHelper.accessor('clientName', {
			header: 'Client',
			size: 90,
			meta: { fixedWidth: true },
			cell: info => {
				const value = info.getValue()
				return <Badge variant='secondary'>{CLIENT_LABELS[value] ?? value}</Badge>
			},
		}) as ColumnDef<ApiMetric>,
	)

	columns.push(
		columnHelper.accessor('timestamp', {
			id: 'timestamp',
			header: 'Time',
			size: 140,
			sortDescFirst: true,
			meta: { fixedWidth: true },
			cell: info => <span>{format(new Date(info.getValue()), 'HH:mm:ss.SSS')}</span>,
		}) as ColumnDef<ApiMetric>,
	)

	return columns
}
